import { endpoints } from '@/utils/endpoints'
import { defineStore } from 'pinia'
import {
  FILE_FROM_SERVER,
  INQUIRIES,
  INQUIRY_COMMENT,
  INQUIRY_FILTERS,
  INQUIRY_ITEM,
  INQUIRY_STATUS,
  Document,
  CustomWindow,
} from '../types'

import { showFormattedInquiryCreatedAt } from '@/utils/matchers'

import { addMinutes, parseISO, set, differenceInHours } from 'date-fns'

declare const window: CustomWindow

export const useInquiriesStore = defineStore('inquiries', () => {
  const authStore = useAuthStore()
  const appStore = useAppStore()

  const inquiries: Ref<INQUIRIES> = ref([])
  const comments: Ref<INQUIRY_COMMENT[]> = ref([])
  const selectedInquiryStatus: Ref<INQUIRY_STATUS | undefined> = ref()
  const isInquiryStatusAutomaticChange = ref(false)
  const attachments = ref<number[]>([])
  const documents = ref<(number | string)[]>([])
  const reusableAttachments = ref<FILE_FROM_SERVER[]>([])
  const temporaryDocuments = ref<Document[]>([])
  const pendingManualHandling = ref<Record<number, number>>({})
  const temporaryAttachments = ref<FILE_FROM_SERVER[]>([])
  const proposalUserEmailComment = ref('')
  const autoResponseStatus: Ref<INQUIRY_STATUS | undefined> = ref()

  const desktopDetailsStore = useDesktopDetailsStore()
  const desktopInquiry = computed(() => desktopDetailsStore.inquiry)
  const desktopComments = computed(() => desktopDetailsStore.comments)
  const setFollowUp = desktopDetailsStore.setFollowUp
  const openArrangePickupModal = ref(false)
  const openAcceptOrderModal = ref(false)

  function setInquiries(allInquiries: INQUIRIES) {
    inquiries.value = allInquiries
  }

  function setInquiryComments(inquiryComments: INQUIRY_COMMENT[]) {
    comments.value = inquiryComments
  }

  function getInquiryById(id: number) {
    return inquiries.value.find((inquiry) => inquiry.id == id)
  }

  function getInquiryCommentById(id: number) {
    return comments.value.find((comment) => comment.id == id)
  }

  function updateAppStoreInquiry(inquiry: INQUIRY_ITEM) {
    const inquiryIndex = inquiries.value.findIndex(
      (inquiryItem) => inquiryItem.id === inquiry.id,
    )

    inquiries.value[inquiryIndex] = inquiry
  }

  async function updateInquiry(updatedInquiry: INQUIRY_ITEM) {
    const index = inquiries.value.findIndex(
      (inquiry) => inquiry.id === updatedInquiry.id,
    )
    const { data } = await useApi(
      `${endpoints().updateInquiry(authStore.spotId, updatedInquiry.id)}`,
    )
      .post(updatedInquiry)
      .json()

    inquiries.value[index] = data.value.inquiries[0]
  }

  async function updateDoorDashDeliveryInquiry(
    updatedInquiry: INQUIRY_ITEM,
    scheduledIn: string,
    pickupDriverInstructions: string,
  ) {
    updatedInquiry.schedule_delivery_in = scheduledIn
    updatedInquiry.pick_up_instructions = pickupDriverInstructions

    await useApi(
      `${endpoints().updateInquiry(authStore.spotId, updatedInquiry.id)}`,
    )
      .post(updatedInquiry)
      .json()
  }

  async function deleteInquiry(inquiryId: number) {
    await useApi(
      `${endpoints().deleteInquiry(authStore.spotId, inquiryId)}`,
    ).delete()

    inquiries.value = inquiries.value.filter(
      (inquiry) => inquiry.id !== inquiryId,
    )
  }

  async function fetchInquiries(
    inquiryType: INQUIRY_FILTERS['inquiryType'],
    filter: INQUIRY_FILTERS['filter'],
    sort: INQUIRY_FILTERS['sort'],
  ) {
    const { data } = await useApi(
      `${endpoints().inquiriesList(authStore.spotId)}/?inquiryType=${inquiryType}&filters=${filter}&sort=${sort}`,
    ).json()

    const inquiries = data.value?.inquiries ?? []

    setInquiries(inquiries)
    return inquiries
  }

  async function fetchInquiryById(inquiryId: string) {
    const { data } = await useApi(
      `${endpoints().getInquiry(authStore.spotId, inquiryId)}`,
    ).json()

    if (data.value?.inquiries) {
      return data.value.inquiries[0]
    }

    return null
  }

  async function createInquiryComment(
    inquiryId: number,
    comment: string,
    responseType: string,
    aiResponseType?: string,
  ) {
    const current_inquiry = getInquiryById(inquiryId)
    const { data } = await useApi(
      `${endpoints().createInquiryComment(authStore.spotId, inquiryId)}`,
    )
      .post({
        text: comment,
        response_type: responseType,
        attachments: appStore.hasEventPlanner ? attachments.value : [],
        documents: appStore.hasEventPlanner ? documents.value : [],
        send_text_message: current_inquiry?.send_text_message,
        ai_response_type: aiResponseType,
      })
      .json()

    attachments.value = []
    documents.value = []
    triggerCustomerFeedback('responded_to_inquiry')
    return data.value.inquiry_comments[0]
  }

  function setSelectedInquiryStatus(inquiryStatus: INQUIRY_STATUS | undefined) {
    selectedInquiryStatus.value = inquiryStatus
  }

  const fetchInquiryProposal = async (inquiryId: number) => {
    try {
      const { data } = await useApi(
        `${endpoints().getProposal(authStore.spotId, inquiryId)}`,
      ).json()

      if (data && data.value) {
        return data.value.linked
      }
      return null
    } catch (error: any) {
      if (error.response && error.response.status === 404) {
        console.error('Resource not found (404)')
      } else {
        console.error('An error occurred', error)
      }

      return null
    }
  }

  const createInquiryProposal = async (body: object, id: number) => {
    const { data } = await useApi(
      `${endpoints().createProposal(authStore.spotId)}`,
    )
      .post(body)
      .json()

     if (data) {
      if (data.value != null) {
        const mobileInquiry = inquiries.value.find((inq) => inq.id === id);
        if (mobileInquiry) {
          mobileInquiry.proposal_count = (mobileInquiry.proposal_count || 0) + 1;
        }
        if(desktopInquiry.value) {
          desktopInquiry.value.proposal_count = (desktopInquiry.value.proposal_count || 0) + 1;
        }
        return data.value.linked
      } 
    }

    return null
  }

  const editInquiryProposal = async (
    proposalPaymentID: string,
    body: object,
  ) => {
    const { data } = await useApi(
      `${endpoints().editProposal(authStore.spotId, proposalPaymentID)}`,
    )
      .post(body)
      .json()

    if (data) {
      if (data.value != null) return data.value.linked
    }

    return null
  }

  const refundPayment = async (proposalPaymentID: string) => {
    await useApi(
      `${endpoints().refundPayment(authStore.spotId, proposalPaymentID)}`,
    )
      .post()
      .json()
  }
  const capturePayment = async (proposalPaymentID: string) => {
    await useApi(
      `${endpoints().capturePayment(authStore.spotId, proposalPaymentID)}`,
    )
      .post()
      .json()
  }
  const sendProposalAttachment = async (proposalID: string, file: FormData) => {
    await useApi(
      `${endpoints().createProposalAttachment(authStore.spotId)}?object_type=proposals&object_id=${proposalID}`,
    )
      .post(file)
      .json()
  }
  const getAttachmentForProposal = async (proposalId: number) => {
    const { data } = await useApi(
      `${endpoints().getInquiryAttachments(authStore.spotId)}?object_type=proposals&object_id=${proposalId}`,
    ).json()
    return data.value.attachments
  }

  const sendProposal = async (
    inquiryId: number,
    comment: string,
    proposalId: number,
  ) => {
    const attachments = await getAttachmentForProposal(proposalId)
    const currentFilesOnServerIDs: number[] = []
    if (attachments) {
      attachments.map((file: FILE_FROM_SERVER) =>
        currentFilesOnServerIDs.push(file.id),
      )
    }

    const { data } = await useApi(
      `${endpoints().createInquiryComment(authStore.spotId, inquiryId)}`,
    )
      .post({
        text: comment,
        attachments: currentFilesOnServerIDs,
        proposal_id: proposalId,
        responseType: 'manual',
      })
      .json()

      if(data.value) {
        desktopComments.value.push(data.value.inquiry_comments[0])
        setFollowUp(true)
        const inquiry = getInquiryById(inquiryId)
        if (inquiry && !['confirmed', 'cancelled'].includes(inquiry.status ?? '')) {
          inquiry.status = 'waiting on customer'
        }
        if (desktopInquiry.value?.id === inquiryId && !['confirmed', 'cancelled'].includes(desktopInquiry.value.status ?? '')) {
          desktopInquiry.value.status = 'waiting on customer'
        }
      }
      
    return data.value
  }

  function getInquiryEndTime(inquiry: INQUIRY_ITEM) {
    if (showFormattedInquiryCreatedAt(inquiry.type)) {
      return parseISO(inquiry.created_at)
    }

    const currentInqTime = set(new Date(), {
      year: inquiry.year!,
      month: inquiry.month! - 1,
      date: inquiry.day!,
      hours: inquiry.hour!,
      minutes: inquiry.min!,
      seconds: 0,
    })

    return addMinutes(currentInqTime as Date, inquiry.duration_min! ?? 0)
  }

  function isLastCommentPast24hrs(inquiry: INQUIRY_ITEM) {
    return (
      inquiry.last_comment &&
      differenceInHours(new Date(), parseISO(inquiry.last_comment.created_at), {
        roundingMethod: 'ceil',
      }) > 24
    )
  }

  function shouldRenderAutoResponse(inquiry: INQUIRY_ITEM) {
    if (inquiry.last_comment?.author_role === 'customer') {
      return false
    }
    return true
  }

  function setOpenArrangePickupModal(value: boolean) {
    openArrangePickupModal.value = value
  }

  function setOpenAcceptOrderModal(value: boolean) {
    openAcceptOrderModal.value = value
  }

  function sendAppStoreReviewEvent(value: boolean) {
    if (!value) return
    if (!localStorage.getItem('device-token')) return
    if (appStore.isAdmin) return

    if (
      window.webkit &&
      window.webkit.messageHandlers &&
      window.webkit.messageHandlers.reviewEventHandler
    ) {
      window.webkit.messageHandlers.reviewEventHandler.postMessage(
        'review_prompt',
      )
    }
    useApi(endpoints().setRequestedAppleReviewDate(authStore.spotId))
      .post({ device_id: localStorage.getItem('device-token') })
      .json()
    if (appStore.spotInfo.should_request_ios_review)
      appStore.spotInfo.should_request_ios_review = false
  }

  return {
    inquiries,
    pendingManualHandling,
    attachments,
    documents,
    reusableAttachments,
    temporaryDocuments,
    temporaryAttachments,
    setInquiries,
    getInquiryById,
    getInquiryCommentById,
    updateInquiry,
    deleteInquiry,
    fetchInquiries,
    fetchInquiryById,
    createInquiryComment,
    setInquiryComments,
    comments,
    setSelectedInquiryStatus,
    selectedInquiryStatus,
    updateAppStoreInquiry,
    fetchInquiryProposal,
    editInquiryProposal,
    createInquiryProposal,
    refundPayment,
    capturePayment,
    sendProposalAttachment,
    isInquiryStatusAutomaticChange,
    getAttachmentForProposal,
    sendProposal,
    updateDoorDashDeliveryInquiry,
    proposalUserEmailComment,
    autoResponseStatus,
    getInquiryEndTime,
    isLastCommentPast24hrs,
    shouldRenderAutoResponse,
    openArrangePickupModal,
    setOpenArrangePickupModal,
    openAcceptOrderModal,
    setOpenAcceptOrderModal,
    sendAppStoreReviewEvent,
  }
})
